import { useDispatch } from 'react-redux';
import { formControlActions } from 'store/formControlSlice/formControlSlice';
import React, { useEffect, useMemo, useCallback } from 'react';
import AIQAutocomplete from '../AIQAutocomplete/AIQAutocomplete';
import AIQInput from '../AIQInput/AIQInput';
import { INVENTORY_PROPERTIES, GATE_IN, isUnknownSentinel } from 'utils/constants';
import { acceptsTypedValue, getFieldColorFromAlerts } from 'utils/lookupHelper';
import useIsSplitViewMode from 'hooks/useIsSplitViewMode';

// Default for `inputValueChangeHandler`, kept as one reference so a free-solo field without a real handler can be detected.
const noInputValueChangeHandler = () => {};

function Select({
    fieldName,
    gateDirection,
    initialValue = null,
    initialReferenceValue,
    value,
    label,
    touched,
    type,
    error,
    formId,
    required,
    lookup,
    restrictToOptions,
    disabled,
    blurHandler = () => {},
    changeHandler = () => {},
    inputValueChangeHandler = noInputValueChangeHandler,
    automation,
    validationField,
    referenceField,
    referenceValue,
    validationStyles,
    validationRules,
    format,
    containerClasses,
    inputContainerClasses,
    forceFieldRegistration,
    helperText,
    isOptionsLoadingInProgress,
    optionsLoadingText,
    forceAutomationStyles,
    issues,
    transactionId,
    camInfo,
    autofillFormFields,
    isClientEquipmentEnabled,
    fieldValueSource,
    aiSuggestions,
    ocrView,
    persistedValidationMap,
    sheetAnnouncementValue,
    isEditTransaction = false,
}) {
    const isInventoryProperty = INVENTORY_PROPERTIES.includes(fieldName);
    const isSplitView = useIsSplitViewMode();

    const dispatch = useDispatch();

    // '?'/'-1'/'UNKNOWN' are display-only "unknown" sentinels (the AI couldn't
    // read a value). They must not become a real field value, otherwise the
    // field looks filled and the required error never fires on submit.
    const sanitizedInitialValue = isUnknownSentinel(initialValue) ? null : initialValue;

    useEffect(() => {
        if (!formId || value !== undefined) return;
        dispatch(
            formControlActions.registerNewFormField({
                formId,
                fieldName,
                initialValue: sanitizedInitialValue,
                ...(!isEditTransaction && disabled && !forceFieldRegistration && { disabled: true }),
                required,
            })
        );
        if (referenceField && initialReferenceValue) {
            dispatch(
                formControlActions.registerNewFormField({
                    formId,
                    fieldName: referenceField,
                    initialValue: initialReferenceValue,
                })
            );
        }

        const hasInitialValue = sanitizedInitialValue || sanitizedInitialValue === false || sanitizedInitialValue === 0;

        if (required && hasInitialValue) {
            dispatch(
                formControlActions.setFieldError({
                    formId,
                    fieldName,
                    error: false,
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, fieldName, formId, initialValue, required, forceFieldRegistration, value]);

    const getOptionLabel = useCallback(
        function (option) {
            if (lookup?.displayExpr) return option?.[lookup.displayExpr];
        },
        [lookup?.displayExpr]
    );

    const hasError = touched && error;
    const message = typeof error === 'string' ? error : helperText || '';
    const acValue =
        lookup?.dataSource.find((data) => {
            let activeOption = false;
            if (Object.hasOwn(data, lookup.referenceExpr) && referenceValue && !isInventoryProperty) {
                activeOption = data?.[lookup.referenceExpr] === referenceValue;
                if (activeOption) return activeOption;
            } else {
                if (Object.hasOwn(data, lookup.displayExpr)) activeOption = data?.[lookup.displayExpr] === value;
                if (Object.hasOwn(data, lookup.valueExpr)) activeOption = data?.[lookup.valueExpr] === value;
            }

            return activeOption;
        }) ?? value;

    const optionAlerts = acValue?.alerts ?? acValue?.equipmentAlerts;
    const hasOptionAlerts = optionAlerts !== undefined && optionAlerts !== null;
    const resolvedColor = hasOptionAlerts ? getFieldColorFromAlerts(optionAlerts) : (validationStyles?.color ?? null);
    const backgroundColor = resolvedColor ? { backgroundColor: resolvedColor } : null;

    const freeSolo = acceptsTypedValue({ lookup, referenceField, restrictToOptions });

    // AIQAutocomplete always shows the stored value, so free-solo text that no handler stores is wiped
    // as soon as the value changes (see its contract). Flag the misconfiguration instead of losing input.
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return;
        if (freeSolo && inputValueChangeHandler === noInputValueChangeHandler) {
            console.warn(`[Select] "${fieldName}" accepts typed text but has no inputValueChangeHandler to store it.`);
        }
    }, [freeSolo, inputValueChangeHandler, fieldName]);

    const options = useMemo(() => {
        return isInventoryProperty && gateDirection === GATE_IN
            ? isClientEquipmentEnabled && fieldName !== 'trailer.equipmentNumber'
                ? lookup?.dataSource?.filter((item) => item.equipmentCategory)
                : []
            : lookup?.dataSource;
    }, [isInventoryProperty, gateDirection, isClientEquipmentEnabled, fieldName, lookup?.dataSource]);

    return (
        <AIQAutocomplete
            valueExpr={lookup?.valueExpr}
            getOptionLabel={lookup?.displayExpr && getOptionLabel}
            value={acValue || null}
            {...(lookup?.referenceExpr && { referenceExpr: lookup?.referenceExpr, initialReferenceValue })}
            onInputChange={inputValueChangeHandler.bind({
                lookup,
                formId,
                fieldName,
                type,
                error,
                required,
                referenceField,
                referenceValue,
                restrictToOptions,
            })}
            onChange={changeHandler.bind({
                lookup,
                formId,
                fieldName,
                type,
                error,
                required,
                referenceField,
                autofillFormFields,
            })}
            onBlur={blurHandler.bind({
                required,
                touched,
                error,
                formId,
                fieldName,
                validationField,
                validationRules,
                format,
            })}
            // Shared with the handler that commits typed text and with CTRL+Enter's fill, so the
            // control cannot offer free input that nothing stores. `restrictToOptions === false` is
            // read strictly: a missing flag is an unconfigured field and stays closed, as before.
            freeSolo={freeSolo}
            isOptionsLoadingInProgress={isOptionsLoadingInProgress}
            optionsLoadingText={optionsLoadingText}
            options={options}
            isInventoryProperty={isInventoryProperty}
            fieldName={fieldName}
            containerClasses={containerClasses}
            isClientEquipmentEnabled={isClientEquipmentEnabled}
            aiSuggestions={ocrView ? ocrView.selectable : isSplitView ? aiSuggestions : undefined}
            ocrGroups={ocrView?.groups}
            renderInput={(params) => {
                return (
                    <AIQInput
                        {...params}
                        helperText={hasError && message}
                        label={label}
                        type={type}
                        error={hasError}
                        name={fieldName}
                        InputLabelProps={{ shrink: true }}
                        automation={automation}
                        disabled={disabled}
                        {...backgroundColor}
                        forceAutomationStyles={forceAutomationStyles}
                        issues={issues}
                        transactionId={transactionId}
                        inputContainerClasses={inputContainerClasses}
                        camInfo={camInfo}
                        isInventoryProperty={isInventoryProperty}
                        fieldValueSource={fieldValueSource}
                        ocrChip={ocrView?.chip}
                        isSplitView={isSplitView}
                        persistedValidationMap={persistedValidationMap}
                        sheetAnnouncementValue={sheetAnnouncementValue}
                    />
                );
            }}
        />
    );
}

export default React.memo(Select);
